import { useQuery } from "@apollo/client/react";
import { toPlainDateString } from "@sa-nu/shared-utils/date";
import type { PlainDateString } from "@sa-nu/shared-utils/date";
import type { PlainDate } from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";

import { graphql } from "../../../../../../__generated__/gql";
import type { buildAccommodationReservationProviderInput } from "../../../../../../utils";
import { UNAVAILABLE_PRICE_LABEL } from "../../../_shared/price-labels";

const AccommodationDayPricesQuery = graphql(`
  query AccommodationDayPricesOnCheckoutDatePickerDrawer(
    $id: ID!
    $firstNight: PlainDate
    $lastNight: PlainDate
    $provider: AccommodationReservationProviderInput
  ) {
    accommodation(id: $id) {
      id
      availabilities(
        firstNight: $firstNight
        lastNight: $lastNight
        provider: $provider
      ) {
        date
        isAvailable
        guaranteedPrice
      }
    }
  }
`);

type UseAccommodationDayPricesParams = {
  accommodationId: string;
  firstNight: PlainDate;
  lastNight: PlainDate;
  provider: ReturnType<typeof buildAccommodationReservationProviderInput>;
};

type UseAccommodationDayPricesResult = {
  /**
   * 日付 → 表示用文字列。
   * 在庫あり: フォーマット済み金額 (guaranteedPrice が無い日は entry なし) /
   * 在庫なし: "-" (guaranteedPrice の有無に関わらず)
   */
  priceMap: Record<PlainDateString, string>;
  /** isAvailable === false の日付セット (idle 時の CI disable 用) */
  unavailableDates: Set<PlainDateString>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * 宿泊施設の日付ごとの料金と空き状況を 1 回 fetch する。
 * Drawer open 時に minDate..maxDate 範囲で一括取得。
 */
export function useAccommodationDayPrices({
  accommodationId,
  firstNight,
  lastNight,
  provider,
}: UseAccommodationDayPricesParams): UseAccommodationDayPricesResult {
  const { data, loading, error } = useQuery(AccommodationDayPricesQuery, {
    variables: {
      id: accommodationId,
      firstNight: toPlainDateString(firstNight),
      lastNight: toPlainDateString(lastNight),
      provider,
    },
  });

  const availabilities = data?.accommodation?.availabilities ?? [];

  const priceMap = availabilities.reduce<Record<PlainDateString, string>>(
    (acc, item) => {
      if (item.date === undefined) return acc;
      if (!item.isAvailable) {
        acc[item.date] = UNAVAILABLE_PRICE_LABEL;
      } else if (item.guaranteedPrice != null) {
        acc[item.date] = formatCurrency(item.guaranteedPrice);
      }
      return acc;
    },
    {},
  );

  const unavailableDates = new Set(
    availabilities.flatMap((item) => {
      if (item.date === undefined || item.isAvailable) return [];
      return [item.date];
    }),
  );

  return {
    priceMap,
    unavailableDates,
    loading,
    error,
  };
}
